import { c as _c } from "react/compiler-runtime";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { FaPlus, FaTrash } from "react-icons/fa6";
import { expandCompoundComponents } from "@/components/dose/dose-form/compound-expand";
import DoseComponentRow from "@/components/dose/dose-form/dose-component-row";
import { DoseFormValues, emptyDoseComponent } from "@/components/dose/dose-form/dose-form-schema";
import { DoseComponentInventoryEntry } from "@/components/dose/dose-form/use-dose-component-inventory";
import Button from "@/components/ui/button";
import { formatQuantity, formatUnit } from "@/lib/format-data";
import { pickMedicationByName } from "@/lib/medication-select";
import { cx } from "@/lib/tailwind-utils";
import { Unit } from "@/types/enums/unit";
import { ORAL_MEDICATION_NAMES } from "@/types/enums/weight-loss-medications";
import { Medication } from "@/types/medication";
interface DoseComponentsProps {
  inventory: DoseComponentInventoryEntry[];
  medications: Medication[];
  onMedicationChange?: (index: number, name: string) => void;
}
export default function DoseComponents(t0) {
  const $ = _c(62);
  const {
    inventory,
    medications,
    onMedicationChange
  } = t0;
  const {
    control,
    setValue,
    formState
  } = useFormContext();
  let t1;
  if ($[0] !== control) {
    t1 = {
      control,
      name: "components",
      keyName: "fieldKey"
    };
    $[0] = control;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    fields,
    append,
    remove,
    replace
  } = useFieldArray(t1);
  let t2;
  if ($[2] !== control) {
    t2 = {
      control,
      name: "components"
    };
    $[2] = control;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const components = useWatch(t2);
  let t3;
  if ($[4] !== control) {
    t3 = {
      control,
      name: "compoundMedicationId"
    };
    $[4] = control;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const compoundMedicationId = useWatch(t3);
  let t4;
  if ($[6] !== compoundMedicationId || $[7] !== medications) {
    t4 = compoundMedicationId ? medications.find(medication => medication.id === compoundMedicationId) ?? null : null;
    $[6] = compoundMedicationId;
    $[7] = medications;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const compound = t4;
  const [showDetails, setShowDetails] = useState(false);
  const autoOpenedFor = useRef(null);
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setShowDetails(false);
      autoOpenedFor.current = null;
    };
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== compoundMedicationId) {
    t6 = [compoundMedicationId];
    $[10] = compoundMedicationId;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  useEffect(t5, t6);
  let t7;
  if ($[12] !== components) {
    t7 = components.some(_temp);
    $[12] = components;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const hasLotCapableIngredient = t7;
  let t8;
  let t9;
  if ($[14] !== compoundMedicationId || $[15] !== hasLotCapableIngredient) {
    t8 = () => {
      if (!compoundMedicationId || !hasLotCapableIngredient) {
        return;
      }
      if (autoOpenedFor.current === compoundMedicationId) {
        return;
      }
      autoOpenedFor.current = compoundMedicationId;
      setShowDetails(true);
    };
    t9 = [compoundMedicationId, hasLotCapableIngredient];
    $[14] = compoundMedicationId;
    $[15] = hasLotCapableIngredient;
    $[16] = t8;
    $[17] = t9;
  } else {
    t8 = $[16];
    t9 = $[17];
  }
  useEffect(t8, t9);
  const hasComponentErrors = Boolean(formState.errors.components);
  let t10;
  let t11;
  if ($[18] !== hasComponentErrors) {
    t10 = () => {
      if (hasComponentErrors) {
        setShowDetails(true);
      }
    };
    t11 = [hasComponentErrors];
    $[18] = hasComponentErrors;
    $[19] = t10;
    $[20] = t11;
  } else {
    t10 = $[19];
    t11 = $[20];
  }
  useEffect(t10, t11);
  let t12;
  if ($[21] !== append || $[22] !== setValue) {
    t12 = () => {
      setValue("compoundMedicationId", null, {
        shouldDirty: true
      });
      append(emptyDoseComponent());
    };
    $[21] = append;
    $[22] = setValue;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const appendComponent = t12;
  let t13;
  if ($[24] !== remove || $[25] !== setValue) {
    t13 = index => {
      setValue("compoundMedicationId", null, {
        shouldDirty: true
      });
      remove(index);
    };
    $[24] = remove;
    $[25] = setValue;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const removeComponent = t13;
  let t14;
  bb0: {
    if (!compound) {
      let t15;
      if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
        t15 = [];
        $[27] = t15;
      } else {
        t15 = $[27];
      }
      t14 = t15;
      break bb0;
    }
    let t15;
    if ($[28] !== components || $[29] !== inventory) {
      let t16;
      if ($[31] !== inventory) {
        t16 = (component_0, index_0) => {
          const parts = [component_0.name || "Medication"];
          if (component_0.amount != null) {
            parts.push(String(component_0.amount));
          }
          if (component_0.unit) {
            parts.push(formatUnit(component_0.unit as Unit));
          }
          const lotSelection = component_0.lotSelection;
          const entry = inventory[index_0];
          const allocated = component_0.lotAllocations ?? entry?.preview?.ownedLots.map(_temp2);
          const lotNumber = allocated?.length ? allocated.map(allocation => {
            const lot_0 = entry?.shipments.find(candidate => candidate.id === allocation.shipmentId);
            const unit = entry?.preview?.stocks.find(stock => stock.stockId === allocation.stockId)?.unit;
            return `${lot_0?.lotNumber || "Missing or unlabeled lot"}: ${formatQuantity(component_0.checkoutReuse ? lot_0?.chargedTodayQuantity ?? 0 : allocation.quantity)}${unit ? ` ${formatUnit(unit)}` : ""}`;
          }).join(", ") : lotSelection?.type === "lot" ? lotSelection.value : "";
          return {
            label: parts.join(" "),
            lotNumber: lotNumber.trim(),
            isLotCapable: !ORAL_MEDICATION_NAMES.includes(component_0.name)
          };
        };
        $[31] = inventory;
        $[32] = t16;
      } else {
        t16 = $[32];
      }
      t15 = components.map(t16);
      $[28] = components;
      $[29] = inventory;
      $[30] = t15;
    } else {
      t15 = $[30];
    }
    t14 = t15;
  }
  const ingredientLines = t14;
  let t15;
  if ($[33] !== ingredientLines) {
    t15 = ingredientLines.filter(_temp3);
    $[33] = ingredientLines;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  const missingLotCount = t15.length;
  const detailsVisible = !compound || showDetails;
  let t16;
  if ($[35] !== medications || $[36] !== onMedicationChange || $[37] !== replace || $[38] !== setValue) {
    t16 = (index_1, name) => {
      const picked = pickMedicationByName(medications, name);
      if (picked?.isCompound) {
        onMedicationChange?.(0, name);
        const ingredientComponents = expandCompoundComponents(picked, medications);
        setValue("compoundMedicationId", picked.id, {
          shouldDirty: true
        });
        replace(ingredientComponents.length > 0 ? ingredientComponents : [emptyDoseComponent(name)]);
        return;
      }
      setValue("compoundMedicationId", null, {
        shouldDirty: true
      });
      onMedicationChange?.(index_1, name);
    };
    $[35] = medications;
    $[36] = onMedicationChange;
    $[37] = replace;
    $[38] = setValue;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  const handleMedicationChange = t16;
  let t17;
  if ($[40] !== compound || $[41] !== hasLotCapableIngredient || $[42] !== ingredientLines || $[43] !== missingLotCount || $[44] !== showDetails) {
    t17 = compound && <div className="space-y-1 py-3"><div className="flex items-center justify-between gap-3"><div className="flex min-w-0 items-center gap-2"><span className="break-words font-medium text-foreground">{compound.name}</span><span className="inline-flex shrink-0 items-center rounded-full bg-status-purple-bg px-2 py-0.5 text-caption-strong text-status-purple-text">Compound</span></div><Button type="button" variant="ghost" size="sm" className="shrink-0" onClick={() => setShowDetails(_temp4)} aria-expanded={showDetails}>{showDetails ? "Hide details" : hasLotCapableIngredient ? "Edit lot numbers" : "Details"}</Button></div>{ingredientLines.length > 0 && <ul className="space-y-0.5">{ingredientLines.map(_temp5)}</ul>}{!showDetails && missingLotCount > 0 && <p className="text-caption text-status-warning-text"><span>{missingLotCount === 1 ? "1 component has no lot number." : `${missingLotCount} components have no lot number.`}</span><span> Open Edit lot numbers to record them.</span></p>}</div>;
    $[40] = compound;
    $[41] = hasLotCapableIngredient;
    $[42] = ingredientLines;
    $[43] = missingLotCount;
    $[44] = showDetails;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== components || $[47] !== detailsVisible || $[48] !== fields || $[49] !== handleMedicationChange || $[50] !== inventory || $[51] !== medications || $[52] !== removeComponent) {
    t18 = detailsVisible && fields.map((field, index_3) => {
      const selectedName = components[index_3]?.name.trim();
      const removeLabel = `Remove component ${index_3 + 1}${selectedName ? ` (${selectedName})` : ""}`;
      return <Fragment key={field.fieldKey}><div className="space-y-3 py-3">{fields.length > 1 && <div className="flex items-center justify-between gap-3"><p className="text-body-strong text-foreground">Component {index_3 + 1}</p><Button type="button" variant="ghost" size="sm" icon={FaTrash} onClick={() => removeComponent(index_3)} aria-label={removeLabel}>Remove component {index_3 + 1}</Button></div>}<DoseComponentRow index={index_3} inventory={inventory[index_3] ?? {
            shipments: [],
            isCheckingInventory: false
          }} medications={medications} onMedicationChange={handleMedicationChange} /></div>{index_3 < fields.length - 1 && <div className="w-full border-t border-border" />}</Fragment>;
    });
    $[46] = components;
    $[47] = detailsVisible;
    $[48] = fields;
    $[49] = handleMedicationChange;
    $[50] = inventory;
    $[51] = medications;
    $[52] = removeComponent;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] !== appendComponent || $[55] !== components || $[56] !== detailsVisible) {
    t19 = detailsVisible && components.some(_temp6) && <Button type="button" className="!mt-0" variant="ghost" size="sm" icon={FaPlus} onClick={appendComponent}>Add component</Button>;
    $[54] = appendComponent;
    $[55] = components;
    $[56] = detailsVisible;
    $[57] = t19;
  } else {
    t19 = $[57];
  }
  let t20;
  if ($[58] !== t17 || $[59] !== t18 || $[60] !== t19) {
    t20 = <div className="space-y-3">{t17}{t18}{t19}</div>;
    $[58] = t17;
    $[59] = t18;
    $[60] = t19;
    $[61] = t20;
  } else {
    t20 = $[61];
  }
  return t20;
}
function _temp6(component_1) {
  return component_1.name.trim();
}
function _temp5(line_0, index_2) {
  return <li key={`${line_0.label}-${index_2}`} className="break-words text-caption text-muted-foreground"><span>{line_0.label}</span>{line_0.isLotCapable && <span className={cx(line_0.lotNumber ? "text-foreground-secondary" : "text-status-warning-text")}>{line_0.lotNumber ? ` — Lot ${line_0.lotNumber}` : " \u2014 No lot recorded"}</span>}</li>;
}
function _temp4(shown) {
  return !shown;
}
function _temp3(line) {
  return line.isLotCapable && !line.lotNumber;
}
function _temp2(lot) {
  return {
    ...lot,
    quantity: lot.outstanding
  };
}
function _temp(component) {
  return !ORAL_MEDICATION_NAMES.includes(component.name);
}